import moment from "moment";
import React, { useCallback, useEffect, useRef, useState } from "react";
import ReactDatePicker from "react-datepicker";
import { useSelector } from "react-redux";
import { USER_API } from "../../common/common";
import { Logout } from "../../common/Funcation";
import { styleObjectGetBG } from "../../common/StyleSeter";
import Loader from "../../components/Loader";
import Pagination from "../../components/Pagination";
import { useIsMobileView } from "../../components/ZoomablePageContent";
import { postApi } from "../../service";

const SPORT_TABS = [
  { key: "cricket", label: "Cricket" },
  { key: "soccer", label: "Soccer" },
  { key: "tennis", label: "Tennis" },
  { key: "virtualT10", label: "Virtual T10" },
];

const formatSport = (type: string) =>
  type ? type.charAt(0).toUpperCase() + type.slice(1) : "";

const formatMatchDate = (item: any) => {
  if (item?.openDate) return item.openDate;
  if (item?.startDate) {
    return moment(item.startDate).format("YYYY-MM-DD HH:mm:ss");
  }
  return "-";
};

const CheckResults = ({ embedded = false }: { embedded?: boolean }) => {
  const DD = useSelector((e: any) => e.domainDetails);
  const [sportType, setSportType] = useState("cricket");
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 2);
    return d;
  });
  const [endDate, setEndDate] = useState(() => new Date());
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [list, setList] = useState<any>({ results: [], totalPages: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const isMobile = useIsMobileView();
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const fetchIdRef = useRef(0);
  const dragRef = useRef({
    active: false,
    startX: 0,
    scrollLeft: 0,
  });

  const fetchResults = useCallback(
    async (pageNum = 1, searchValue = "") => {
      const fetchId = ++fetchIdRef.current;
      setIsLoading(true);
      setPage(pageNum);
      const data = {
        api: USER_API.MATCH_RESULT,
        value: {
          type: sportType,
          page: String(pageNum),
          limit: "50",
          fromDate: startDate.toISOString(),
          toDate: endDate.toISOString(),
          ...(searchValue ? { search: searchValue } : {}),
        },
      };

      try {
        const response = await postApi(data);
        if (fetchId !== fetchIdRef.current) return;
        setList(response.data.data || { results: [], totalPages: 0 });
      } catch (err: any) {
        if (fetchId !== fetchIdRef.current) return;
        setList({ results: [], totalPages: 0 });
        if (err?.response?.data?.statusCode === 401) {
          Logout();
        }
      } finally {
        if (fetchId === fetchIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [sportType, startDate, endDate]
  );

  const beginLoading = () => {
    setIsLoading(true);
  };

  const handleSportChange = (nextSport: string) => {
    if (nextSport === sportType) return;
    beginLoading();
    setSportType(nextSport);
  };

  const handleStartDateChange = (date: Date | null) => {
    if (!date) return;
    beginLoading();
    setStartDate(date);
  };

  const handleEndDateChange = (date: Date | null) => {
    if (!date) return;
    beginLoading();
    setEndDate(date);
  };

  useEffect(() => {
    beginLoading();
    const timer = setTimeout(() => {
      fetchResults(1, search);
    }, search ? 350 : 0);
    return () => clearTimeout(timer);
  }, [sportType, startDate, endDate, search, fetchResults]);

  const handlePageClick = (e: any) => {
    fetchResults(e.selected + 1, search);
  };

  const handleSearchSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    fetchResults(1, search);
  };

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = tableWrapRef.current;
    if (!el || el.scrollWidth <= el.clientWidth) return;
    dragRef.current = {
      active: true,
      startX: e.clientX,
      scrollLeft: el.scrollLeft,
    };
    el.classList.add("is-dragging");
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const el = tableWrapRef.current;
    if (!el || !dragRef.current.active) return;
    el.scrollLeft =
      dragRef.current.scrollLeft - (e.clientX - dragRef.current.startX);
  };

  const endDrag = () => {
    dragRef.current.active = false;
    tableWrapRef.current?.classList.remove("is-dragging");
  };

  const results = list?.results || [];
  const startIndex = (page - 1) * 50;
  const hideOverlayLoader = embedded && isMobile;

  return (
    <div className={`check-results${embedded ? " is-embedded" : ""}`}>
      {isLoading && !hideOverlayLoader && (
        <div className="loader_top loader_overlay">
          <Loader />
        </div>
      )}

      <div className="check-results_title">Check Results</div>

      <div className="check-results_filters">
        <div className="check-results_dates">
          <div className="check-results_date">
            <label>from</label>
            <div className="check-results_date_input">
              <ReactDatePicker
                selected={startDate}
                onChange={handleStartDateChange}
                showTimeSelect
                timeIntervals={15}
                dateFormat="MM/dd/yyyy hh:mm:ss aa"
                className="form-control hasDatepicker check-results_date_field"
                popperProps={{ strategy: "fixed" } as any}
                maxDate={endDate}
              />
              <span className="check-results_date_icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path
                    fill="currentColor"
                    d="M7 2h2v2h6V2h2v2h3a1 1 0 011 1v15a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1h3V2zm12 8H5v10h14V10zM5 8h14V6H5v2z"
                  />
                </svg>
              </span>
            </div>
          </div>
          <div className="check-results_date">
            <label>to</label>
            <div className="check-results_date_input">
              <ReactDatePicker
                selected={endDate}
                onChange={handleEndDateChange}
                showTimeSelect
                timeIntervals={15}
                dateFormat="MM/dd/yyyy hh:mm:ss aa"
                className="form-control hasDatepicker check-results_date_field"
                popperProps={{ strategy: "fixed" } as any}
                minDate={startDate}
              />
              <span className="check-results_date_icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" width="16" height="16">
                  <path
                    fill="currentColor"
                    d="M7 2h2v2h6V2h2v2h3a1 1 0 011 1v15a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1h3V2zm12 8H5v10h14V10zM5 8h14V6H5v2z"
                  />
                </svg>
              </span>
            </div>
          </div>
        </div>

        <div className="check-results_sports">
          {SPORT_TABS.map((item) => {
            const active = sportType === item.key;
            return (
              <button
                key={item.key}
                type="button"
                className={`check-results_sport${active ? " active" : ""}`}
                style={styleObjectGetBG(DD?.colorSchema, true, true)}
                onClick={() => handleSportChange(item.key)}
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>

      <form className="check-results_search" onSubmit={handleSearchSubmit}>
        <input
          type="search"
          placeholder="Search..."
          value={search}
          className="check-results_search_field"
          onChange={(e) => setSearch(e.target.value)}
        />
        <button type="submit" aria-label="Search">
          <svg width="18" height="18" viewBox="0 0 19 19" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M12.547 11.543H12l-.205-.172a4.539 4.539 0 001.06-2.914A4.442 4.442 0 008.41 4C5.983 4 4 5.989 4 8.457a4.442 4.442 0 004.445 4.457c1.094 0 2.12-.411 2.905-1.062l.206.171v.548L14.974 16 16 14.971l-3.453-3.428zm-4.102 0a3.069 3.069 0 01-3.077-3.086 3.068 3.068 0 013.077-3.086 3.069 3.069 0 013.076 3.086 3.069 3.069 0 01-3.076 3.086z"
              fill="#1e1e1e"
            />
          </svg>
        </button>
      </form>

      <div
        className={`check-results_table_wrap${isLoading ? " is-loading" : ""}`}
        ref={tableWrapRef}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
        onPointerCancel={endDrag}
      >
        <table className="table01 check-results_table">
          <thead>
            <tr>
              <th className="check-results_sticky col-sno">S.No.</th>
              <th className="check-results_sticky col-sport">Sport</th>
              <th>Match Date</th>
              <th>Match Name</th>
              <th>Winner</th>
            </tr>
          </thead>
          <tbody>
            {results.length > 0 ? (
              results.map((item: any, i: number) => (
                <tr key={item._id || `${item.gameId}-${i}`}>
                  <td className="check-results_sticky col-sno">
                    {startIndex + i + 1}
                  </td>
                  <td className="check-results_sticky col-sport">
                    {formatSport(item?.type)}
                  </td>
                  <td>{formatMatchDate(item)}</td>
                  <td className="col-name">{item?.name || "-"}</td>
                  <td className="col-winner">{item?.winner || "-"}</td>
                </tr>
              ))
            ) : !isLoading ? (
              <tr>
                <td colSpan={5} className="check-results_empty">
                  No data
                </td>
              </tr>
            ) : (
              Array.from({ length: 2 }, (_, i) => (
                <tr key={`loading-${i}`}>
                  <td colSpan={5}>&nbsp;</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {list?.totalPages > 1 && (
        <div className="check-results_pager">
          <Pagination
            handlePageClick={handlePageClick}
            totalPages={list.totalPages}
          />
        </div>
      )}
    </div>
  );
};


export default CheckResults;
